// Build-time seed for every list on dubai-todo.com — the Dubai counterpart of
// apps/riviera/src/lib/detail/list-prefetch.ts, much smaller: one city; the
// guides (Sanity articles) are fetched by @todo/web-ui/lib/guides.
//
// The pages are prerendered (web.output "static"), and effects don't run
// during the export, so a list fetched in an effect would ship as an empty
// page. Instead this module runs every list query once per export process and
// parks the rows in @todo/core's static-prefetch cache; each page renders its
// cards from it and bakes the same rows into its HTML (seedScript), which
// useTours / useBoats read back on the client's first render so hydration
// matches. Called from app/[lang]/_layout.tsx's generateStaticParams.
//
// Every query is the one the page's hook would issue (list-queries.ts builds
// both), so the seed and a client fetch can never disagree. The event lists
// are the exception: they're read only from their seeds, and a date filter on
// the hub asks event_shows_filtered in the browser (eventsFilteredArgs).

import type { Boat, BoatTranslations } from '@todo/core/boats';
import type { BoatsQuery } from '@todo/core/hooks/use-boats';
import type { ToursQuery } from '@todo/core/hooks/use-tours';
import { SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { LIST_BUCKET, boatsRpcArgs, toursRpcArgs, toursRpcName } from '@todo/core/list-queries';
import { setPrefetched } from '@todo/core/static-prefetch';
import { getDateRange } from '@todo/core/date-range';
import { supabase, type DbEvent, type LocaleCopy } from '@todo/core/supabase';
import type { Tour, TourTranslations } from '@todo/core/tours';

import { COLLECTIONS, YACHT_THEME } from '@/lib/collections';
import {
  COLLECTION_DUPLICATES_SEED_KEY,
  COLLECTION_TOURS_LIMIT,
  HOME_CATEGORIES_SEED_KEY,
  collectionBySlug,
  collectionSeedKey,
  findContainedLists,
  findDuplicateLists,
  type CategoryStat,
} from '@todo/web-ui/lib/collections';
import { prefetchGuides } from '@todo/web-ui/lib/guides';
import en from '@/lib/i18n/resources/en.json';
import { SITE } from '@site';

const CITY = SITE.cityIds[0];

/** Every tour, best ranked first (tours_by_city's tour_rank_score order). */
export const TOURS_INDEX_QUERY: ToursQuery = { city: CITY, limit: 500 };
export const TOURS_INDEX_SEED_KEY = 'tours-index';

/**
 * The /boats hub ("Yachts & Boats"): every yacht_charter tour — Click&Boat's
 * boat trips and Viator's yacht tours — best ranked first (tours_by_theme).
 * Day rentals (public.boats) aren't connected on this site.
 */
export const YACHT_TOURS_QUERY: ToursQuery = { theme: YACHT_THEME, city: CITY, limit: 300 };
export const YACHT_TOURS_SEED_KEY = 'yacht-tours';

/**
 * The home page's "Featured This Week" row: four cards, the top boat and the
 * best-ranked tours — its own small seeds, so the home page doesn't bake the
 * whole catalog to show four cards.
 */
export const FEATURED_CARDS = 4;
export const FEATURED_TOURS_QUERY: ToursQuery = { city: CITY, limit: FEATURED_CARDS };
export const FEATURED_TOURS_SEED_KEY = 'home-tours';
export const FEATURED_BOATS_QUERY: BoatsQuery = { city: CITY, limit: 1 };
export const FEATURED_BOATS_SEED_KEY = 'home-boats';

/**
 * The /events hub: every show of the next EVENTS_WINDOW_DAYS days, one card
 * per show (its soonest date in the window), "recommended" first — list_rank,
 * Platinumlist's popularity with exclusive events ranked lower — then soonest.
 * Card fields only (event_shows_filtered). ~340 shows: the hub filters them by
 * category in the browser, and asks the RPC again for a date window.
 */
export const EVENTS_WINDOW_DAYS = 180;
export const EVENTS_INDEX_SEED_KEY = 'events-index';
/** The home page's "Events this week": the top FEATURED_CARDS of the next 7 days. */
export const HOME_EVENTS_SEED_KEY = 'home-events';
export const HOME_EVENTS_WHEN = 'week';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A show page's "More events like this": the top RELATED_EVENTS + 1 shows of
 * its category (one may be the page's own), sliced from the hub list — a few
 * rows baked into each show page rather than the whole list.
 */
export const RELATED_EVENTS = 4;
export function relatedEventsSeedKey(category: string | null): string {
  return `related-events:${category ?? 'other'}`;
}

/**
 * event_shows_filtered's arguments for the site's city, from the start of
 * today (Dubai days) — at build time for the seeds, in the browser for the
 * hub's date filter (when: a getDateRange id; 'any' = the hub's whole window).
 */
export function eventsFilteredArgs(when: string, category: string | null = null) {
  const range =
    when === 'any'
      ? (() => {
          const { from } = getDateRange('alltime');
          return { from, until: new Date(from.getTime() + EVENTS_WINDOW_DAYS * DAY_MS) };
        })()
      : getDateRange(when);
  return {
    p_city: CITY,
    p_from: range.from.toISOString(),
    p_until: range.until.toISOString(),
    p_category: category,
    p_min_price: null,
    p_max_price: null,
    p_sort: 'recommended',
  };
}

export async function fetchEventCards(args: ReturnType<typeof eventsFilteredArgs>, limit?: number): Promise<DbEvent[]> {
  const query = supabase.rpc('event_shows_filtered', args);
  const { data, error } = await (limit ? query.limit(limit) : query);
  if (error) throw new Error(`event_shows_filtered: ${error.message}`);
  return (data ?? []) as DbEvent[];
}

/**
 * The /new-years-eve hub's lists, picked by title — no ids, no theme rule:
 * Click&Boat trips get only their raw_contains theme (tour_themes()), so a
 * keyword rule couldn't reach the Click&Boat New Year cruise. Events: shows of
 * the /events list named for the night ("New Year's Eve", "NYE", "Countdown",
 * "Silvester") whose date is 31 December or 1 January in Dubai — not the
 * brunches and ladies' days that merely fall on it. Tours: every live tour
 * whose title says so, as tours_by_city ranks them. Out of season (from
 * 2 January until next autumn's events are on sale) both lists come out empty
 * or short, and the page keeps its copy.
 */
export const NYE_EVENTS_SEED_KEY = 'nye-events';
export const NYE_TOURS_SEED_KEY = 'nye-tours';
/** The home page's New Year's Eve row: the top FEATURED_CARDS of NYE_EVENTS. */
export const HOME_NYE_SEED_KEY = 'home-nye';
const NYE_TITLE = /new ?years?|countdown|silvester|\bnye\b/i;
/** NYE_TITLE as a Postgres regex (PostgREST imatch = ~*). */
const NYE_TITLE_PG = 'new ?years?|countdown|silvester|\\mnye\\M';
/** More than the catalog holds: the title filter runs over the whole ranked list. */
const NYE_TOURS_SCAN = 2000;

function dubaiMonthDay(ts: string): string {
  // en-CA formats as YYYY-MM-DD.
  return new Date(ts).toLocaleDateString('en-CA', { timeZone: SITE.timeZone }).slice(5);
}

export function isNyeEvent(e: DbEvent): boolean {
  const day = dubaiMonthDay(e.start_ts);
  return (day === '12-31' || day === '01-01') && NYE_TITLE.test(e.title);
}

/** The hub's events, from the /events list (at build time) or event_shows_filtered (in the browser). */
export async function fetchNyeEvents(): Promise<DbEvent[]> {
  return (await fetchEventCards(eventsFilteredArgs('any'))).filter(isNyeEvent);
}

export async function fetchNyeTours(): Promise<Tour[]> {
  const { data, error } = await supabase
    .rpc('tours_by_city', toursRpcArgs({ city: CITY, limit: NYE_TOURS_SCAN }))
    .select(CARD_COLUMNS)
    .filter('title', 'imatch', NYE_TITLE_PG);
  if (error) throw new Error(`prefetch(nye tours): ${error.message}`);
  return ((data ?? []) as unknown as CardRow[]).map(cardTour);
}

export function collectionQuery(theme: string): ToursQuery {
  return { theme, city: CITY, limit: COLLECTION_TOURS_LIMIT };
}

// The seed is baked into every page that shows the list, so it carries only
// what a card renders: per-locale titles, not the descriptions and summaries
// that make up most of a full row.
function trimCopy(copy: LocaleCopy | undefined): LocaleCopy | undefined {
  return copy ? { title: copy.title, description: '', summary: '' } : undefined;
}

function trimTranslations<T extends TourTranslations | BoatTranslations>(t: T | null | undefined): T | null {
  if (!t) return null;
  const out: Record<string, LocaleCopy | undefined> = {};
  for (const locale of SUPPORTED_LOCALES) out[locale] = trimCopy(t[locale]);
  return out as unknown as T;
}

/**
 * The columns of a tour card, asked of the list RPC with `.select()`, so
 * PostgREST serializes only these: the whole rows (raw, details and the long
 * copy in three languages, ~15 MB for every Dubai tour) ran past the anon
 * role's 3 s statement timeout. Per locale only the title, as `<locale>_title`.
 */
const CARD_COLUMNS = [
  'id,source,title,city_id,rating,review_count,duration,from_price,currency,image_url',
  ...SUPPORTED_LOCALES.map((locale) => `${locale}_title:translations->${locale}->>title`),
].join(',');

type CardRow = Pick<
  Tour,
  'id' | 'source' | 'title' | 'city_id' | 'rating' | 'review_count' | 'duration' | 'from_price' | 'currency' | 'image_url'
> &
  Record<string, string | null>;

function cardTour(row: CardRow): Tour {
  const translations: Record<string, LocaleCopy | undefined> = {};
  for (const locale of SUPPORTED_LOCALES) {
    const title = row[`${locale}_title`];
    translations[locale] = title ? { title, description: '', summary: '' } : undefined;
  }
  return {
    id: row.id,
    source: row.source,
    source_id: '',
    title: row.title,
    description: null,
    city_id: row.city_id,
    rating: row.rating,
    review_count: row.review_count,
    duration: row.duration,
    from_price: row.from_price,
    currency: row.currency,
    image_url: row.image_url,
    booking_url: null,
    delisted_at: null,
    translations: translations as unknown as TourTranslations,
  };
}

function trimBoat(boat: Boat): Boat {
  return {
    id: boat.id,
    source: boat.source,
    source_id: '',
    name: boat.name,
    boat_type: boat.boat_type,
    city_id: boat.city_id,
    rating: boat.rating,
    year: boat.year,
    owner_type: null,
    from_price: boat.from_price,
    currency: boat.currency,
    image_url: boat.image_url,
    booking_url: null,
    has_owner_text: boat.has_owner_text,
    delisted_at: null,
    translations: trimTranslations(boat.translations),
  };
}

async function fetchTours(query: ToursQuery): Promise<Tour[]> {
  const { data, error } = await supabase.rpc(toursRpcName(query), toursRpcArgs(query)).select(CARD_COLUMNS);
  if (error) throw new Error(`prefetch(${toursRpcName(query)}): ${error.message}`);
  return ((data ?? []) as unknown as CardRow[]).map(cardTour);
}

async function fetchBoats(query: BoatsQuery): Promise<Boat[]> {
  const { data, error } = await supabase.rpc('boats_by_city', boatsRpcArgs(query));
  if (error) throw new Error(`prefetch(boats_by_city): ${error.message}`);
  return ((data ?? []) as Boat[]).map(trimBoat);
}

/**
 * Runs `tasks` at most `limit` at a time, results in task order. Fired all at
 * once, the ~25 list queries — whole tours rows with their long copy in three
 * languages — ran into the project's statement timeout.
 */
async function pooled<T>(tasks: (() => Promise<T>)[], limit = 4): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      results[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
}

/**
 * Whether a collection (or /boats, `collections.boats`) has a guide — read
 * from the English copy, written in all three languages at once.
 */
function hasGuide(slug: string): boolean {
  const copy = (en.collections as Record<string, { guide?: unknown[] } | string>)[slug];
  return typeof copy === 'object' && !!copy.guide?.length;
}

/**
 * The build's near-copy verdict over /boats and every collection
 * (findDuplicateLists), seeded for the collection pages' `indexable`; the
 * pairs close to it go to the export log.
 */
function seedDuplicates(yachtTours: Tour[], collections: Tour[][]): void {
  const pages = [
    { slug: 'boats', hasGuide: hasGuide('boats'), ids: yachtTours.map((t) => t.id) },
    ...COLLECTIONS.map((c, i) => ({ slug: c.slug, hasGuide: hasGuide(c.slug), ids: collections[i].map((t) => t.id) })),
  ];
  const { duplicateOf, similar } = findDuplicateLists(pages);
  setPrefetched(LIST_BUCKET, COLLECTION_DUPLICATES_SEED_KEY, duplicateOf);
  for (const { slug, other, similarity } of similar) {
    const verdict = duplicateOf[slug] === other ? 'noindex: a copy of' : 'similar to';
    console.log(`[collections] /${slug} ${verdict} /${other} (${Math.round(similarity * 100)}% of their tours shared)`);
  }
  // A page lying inside another (not its parent): fine only while their
  // titles answer different searches — /family and /attractions, /old-dubai
  // and /city-tours (#127).
  for (const { slug, other, shared, count } of findContainedLists(pages, (s) => collectionBySlug(s)?.parent)) {
    console.log(`[collections] /${slug} inside /${other} (${shared} of its ${count} tours) — keep their titles apart`);
  }
}

let prefetchPromise: Promise<void> | null = null;

/** Fetches and seeds every list. Memoized: one round of queries per export. */
export function prefetchLists(): Promise<void> {
  if (!prefetchPromise) {
    prefetchPromise = (async () => {
      await prefetchGuides();
      const featuredBoats = await fetchBoats(FEATURED_BOATS_QUERY);
      const [events, homeEvents] = await Promise.all([
        fetchEventCards(eventsFilteredArgs('any')),
        fetchEventCards(eventsFilteredArgs(HOME_EVENTS_WHEN), FEATURED_CARDS),
      ]);
      setPrefetched(LIST_BUCKET, EVENTS_INDEX_SEED_KEY, events);
      const byCategory = new Map<string, DbEvent[]>();
      for (const e of events) {
        const key = relatedEventsSeedKey(e.category_id);
        const rows = byCategory.get(key) ?? [];
        if (rows.length <= RELATED_EVENTS) rows.push(e);
        byCategory.set(key, rows);
      }
      for (const [key, rows] of byCategory) setPrefetched(LIST_BUCKET, key, rows);
      setPrefetched(LIST_BUCKET, HOME_EVENTS_SEED_KEY, homeEvents);
      const nyeEvents = events.filter(isNyeEvent);
      setPrefetched(LIST_BUCKET, NYE_EVENTS_SEED_KEY, nyeEvents);
      setPrefetched(LIST_BUCKET, HOME_NYE_SEED_KEY, nyeEvents.slice(0, FEATURED_CARDS));
      const [tours, yachtTours, featuredTours, nyeTours, ...collections] = await pooled([
        () => fetchTours(TOURS_INDEX_QUERY),
        () => fetchTours(YACHT_TOURS_QUERY),
        () => fetchTours(FEATURED_TOURS_QUERY),
        () => fetchNyeTours(),
        ...COLLECTIONS.map((c) => () => fetchTours(collectionQuery(c.theme))),
      ]);
      setPrefetched(LIST_BUCKET, TOURS_INDEX_SEED_KEY, tours);
      setPrefetched(LIST_BUCKET, YACHT_TOURS_SEED_KEY, yachtTours);
      setPrefetched(LIST_BUCKET, FEATURED_TOURS_SEED_KEY, featuredTours);
      setPrefetched(LIST_BUCKET, FEATURED_BOATS_SEED_KEY, featuredBoats);
      setPrefetched(LIST_BUCKET, NYE_TOURS_SEED_KEY, nyeTours);
      console.log(`[new-years-eve] ${nyeEvents.length} events, ${nyeTours.length} tours`);
      COLLECTIONS.forEach((c, i) => setPrefetched(LIST_BUCKET, collectionSeedKey(c.slug), collections[i]));
      seedDuplicates(yachtTours, collections);
      const categoryStats: CategoryStat[] = COLLECTIONS.flatMap((c, i) =>
        c.kind === 'category'
          ? [{ slug: c.slug, count: collections[i].length, image: collections[i][0]?.image_url ?? null }]
          : [],
      );
      setPrefetched(LIST_BUCKET, HOME_CATEGORIES_SEED_KEY, categoryStats);
    })();
  }
  return prefetchPromise;
}
